<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="700px"
  >
    <template v-slot:activator="{ on: dialog }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            style="max-width:36px !important"
            class="ml-2"
            icon
            v-on="{ ...dialog, ...tooltip }"
          >
            <v-icon color="hrissecondary">
              mdi-plus-circle
            </v-icon>
          </v-btn>
        </template>
        <span>Add entry</span>
      </v-tooltip>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Claim Type"
      text="Add Claim Type"
      class="mb-0"
    >
      <form v-if="dialog">
        <v-container>
          <v-row no-gutters>
            <v-col
              cols="12"
              class="px-2"
            >
              <v-autocomplete
                v-model="claimType"
                v-validate="'required'"
                :error-messages="errors.collect('claim_type_id')"
                :items="claimEntitlements"
                data-vv-name="claim_type_id"
                data-vv-as="claim type"
                item-text="name"
                item-value="id"
                return-object
                label="Claim Type"
                outlined
              />     
            </v-col>
          </v-row>

          <div v-if="claimType">
            <v-row
              v-show="claimType.requires_receipt"
              no-gutters
            >
              <v-col
                cols="6"
                class="px-2"
              >
                <v-menu
                  v-model="receiptDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="receiptDate"
                      v-validate="claimType.requires_receipt ? 'required' : ''"
                      label="Receipt Date"
                      :error-messages="errors.collect('receipt_date')"
                      data-vv-name="receipt_date"  
                      data-vv-as="date"      
                      readonly
                      outlined
                      class="label-position"
                      v-on="on"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>
                          mdi-calendar
                        </v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="receiptDate"
                    :min="minReceiptDate"
                    :max="maxReceiptDate"
                    @input="receiptDateMenu = false"
                  />
                </v-menu>
              </v-col>

              <v-col
                cols="6"
                class="px-2"
              >
                <v-text-field
                  v-model="receiptNumber"
                  v-validate="claimType.requires_receipt ? 'required' : ''"
                  :error-messages="errors.collect('receipt_no')"
                  data-vv-name="receipt_no"
                  data-vv-as="receipt number"
                  label="Receipt No."
                  outlined
                />
              </v-col>
            </v-row>

            <v-row 
              v-if="claimType.claim_entitlement_settings && claimType.claim_entitlement_settings.credit_unit === 'month'"
              no-gutters
            >
              <v-col
                cols="12"
                class="px-2"
              >
                <v-menu
                  v-model="claimMonthMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="claimMonthDisplay"
                      v-validate="'required'"
                      label="Claim for"
                      :error-messages="errors.collect('claimMonthDisplay')"
                      :disabled="claimType.requires_receipt ? !receiptDate : !claimType"
                      data-vv-name="claimMonthDisplay"  
                      data-vv-as="claim month"      
                      readonly
                      outlined
                      class="label-position"
                      v-on="on"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>
                          mdi-calendar
                        </v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="claimMonth"
                    type="month"
                    :min="claimType.requires_receipt ? receiptDate : ''"
                    :max="!claimType.requires_receipt ? today : ''"
                    @input="claimMonthMenu = false"
                  />
                </v-menu>
              </v-col>
            </v-row>

            <v-row
              v-if="claimType.details.length > 0"
              no-gutters
            >
              <v-col
                class="px-2"
              >
                <v-autocomplete
                  v-model="detail"
                  v-validate="claimType.details.length > 0 ? 'required' : ''"
                  :items="claimType.details"
                  data-vv-name="detail_id"
                  :error-messages="errors.collect('detail_id')"
                  item-text="name"
                  item-value="id"
                  outlined
                  return-object
                  @input="claimDetailChanged(detail)"
                />     
              </v-col>
            </v-row>

            <v-row
              v-for="field in (claimType.custom_fields && claimType.custom_fields.length > 0 ? claimType.custom_fields : [])"
              :key="field.id"
              no-gutters
            >
              <v-col
                cols="12"
                class="px-2"
              >
                <!-- Text -->
                <v-text-field
                  v-if="field.type === 'Text'"
                  v-model="customFieldValues[field.id]"
                  v-validate="field.is_mandatory ? 'required' : ''"
                  :data-vv-name="field.name"
                  :data-vv-as="field.name.toLowerCase()"
                  :error-messages="errors.collect(field.name)"
                  :label="field.name"
                  outlined
                />

                <!-- Date -->
                <v-menu
                  v-else-if="field.type === 'Date'"
                  v-model="datePickers[field.id]"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="customFieldValues[field.id]"
                      v-validate="field.is_mandatory ? 'required' : ''"
                      :data-vv-name="`custom_field_${field.id}`"
                      :data-vv-as="field.name.toLowerCase()"
                      :error-messages="errors.collect(`custom_field_${field.id}`)"
                      :label="field.name"
                      class="label-position"
                      readonly
                      outlined
                      v-on="on"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>
                          mdi-calendar
                        </v-icon>
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="customFieldValues[field.id]"
                    @input="onDateSelected(field)"
                  />
                </v-menu>

                <!-- Selection -->
                <v-autocomplete
                  v-else-if="field.type === 'Selection'"
                  v-model="customFieldValues[field.id]"
                  v-validate="field.is_mandatory ? 'required' : ''"
                  :items="field.selections"
                  :data-vv-name="field.name"
                  :data-vv-as="field.name.toLowerCase()"
                  :error-messages="errors.collect(field.name)"
                  item-text="name"
                  item-value="id"
                  :label="field.name"
                  outlined
                />
                
                <!-- Number -->
                <v-text-field
                  v-else-if="field.type === 'Number'"
                  v-model="customFieldValues[field.id]"
                  v-validate="field.is_mandatory ? 'required' : ''"
                  :data-vv-name="field.name"
                  :data-vv-as="field.name.toLowerCase()"
                  :error-messages="errors.collect(field.name)"
                  :label="field.name"
                  type="number"
                  outlined
                  min="0"
                  @keydown.native="['e', 'E', '+'].includes($event.key) && $event.preventDefault()"
                />
              </v-col>
            </v-row>
            
            <v-row
              v-if="claimType && !!claimType.has_limit && claimType.claim_entitlement_settings && claimType.claim_entitlement_settings.credit_unit === 'day'"
              no-gutters
            >
              <v-col
                cols="12"
                class="px-2"
              >
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="dateRangeDisplay"
                      label="Claim Period"
                      readonly
                      class="label-position"
                      clearable
                      outlined
                      prepend-inner-icon="mdi-calendar"
                      v-on="on"
                      @click:clear="
                        dates = [];
                        if (claimType.claim_entitlement_settings.allow_claim_half_entitlement) {
                          quantity = 0
                        }
                      "
                    />
                  </template>
                  <v-date-picker
                    v-model="dates"
                    :min="minReceiptDate"
                    :max="claimType.allow_advance_purchase ? null : today"
                    range
                    no-title
                    scrollable
                    :allowed-dates="allowedDates"
                  />
                </v-menu>
                <v-col
                  v-if="noDateSelected"
                  cols="12"
                  class="mt-n10"
                >
                  <span
                    class="red--text text--lighten-1 caption"
                  >
                    Please select at least one date.
                  </span>
                </v-col>
              </v-col>
              <v-col
                v-if="claimType.claim_entitlement_settings.allow_claim_half_entitlement && dates.length > 1"
                cols="12"
                class="px-2 py-0 mt-n4 mb-3"
              >
                <v-simple-table 
                  dense 
                  outlined
                >
                  <thead>
                    <tr>
                      <th class="text-left">
                        Date
                      </th>
                      <th class="text-left">
                        Half?
                      </th>
                      <th class="text-right">
                        Claim Amount (RM)
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr 
                      v-for="(dateClaim, counter) in dateClaims" 
                      :key="counter"
                    >
                      <!-- Date -->
                      <td>{{ dateClaim.date }}</td>

                      <!-- Half entitlement -->
                      <td>
                        <v-checkbox
                          v-model="dateClaim.is_half_entitlement"
                          :name="'is_half_entitlement' + counter"
                          color="green"
                          hide-details
                          class="mt-0 pa-0"
                          @change="recalculateTotals()"
                        />
                      </td>

                      <!-- Amount to deduct -->
                      <td class="text-right">
                        {{ formatMoney(dateClaim.amount_to_deduct ) }}
                      </td>
                    </tr>
                  </tbody>

                  <tfoot>
                    <tr>
                      <td
                        colspan="3"
                        class="text-right font-weight-bold"
                      >
                        Total = {{ formatMoney(totalAmount ) }}
                      </td>
                    </tr>
                  </tfoot>
                </v-simple-table>
              </v-col>

              <v-col
                v-if="claimType && claimType.claim_entitlement_settings && claimType.claim_entitlement_settings.allow_combine_entitlement"
                cols="12"
                class="px-2"
              >
                <v-autocomplete
                  v-model="combineEntitlementEmployees"
                  :error-messages="errors.collect('employees')"
                  :items="departmentEmployees"
                  data-vv-name="employees"
                  data-vv-as="employees"
                  item-text="person.name"
                  item-value="employee_uid"
                  :disabled="dates.length <= 0"
                  label="Combine entitlement with"
                  placeholder="Select colleagues to combine entitlements if any"
                  outlined
                  hide-no-data
                  hide-selected
                  small-chips
                  multiple
                  deletable-chips
                  clearable
                />
              </v-col>
            </v-row>

            <v-row 
              v-if="!(claimType && !!claimType.has_limit && claimType.claim_entitlement_settings && claimType.claim_entitlement_settings.credit_unit === 'day' && claimType.claim_entitlement_settings.allow_claim_half_entitlement)"
              no-gutters
            >
              <v-col
                cols="4"
                class="px-2"
              >
                <v-text-field
                  v-model="quantity"
                  v-validate="'required|checkDecimalPlace|validAmount'"
                  type="number"
                  :error-messages="errors.collect('quantity')"
                  data-vv-name="quantity"
                  data-vv-as="quantity"
                  label="Unit/km"
                  :readonly="claimType && claimType.claim_entitlement_settings && claimType.claim_entitlement_settings.credit_unit === 'day' ? true : false"
                  outlined
                  min="1"
                  @keydown.native="['e', 'E', '+', '-'].includes($event.key) && $event.preventDefault()"
                />
              </v-col>

              <v-col
                cols="4"
                class="px-2"
              >
                <v-text-field
                  :key="amountKey"
                  v-model="amount"
                  v-money="money"
                  v-validate="'required|validAmount'"
                  :error-messages="errors.collect('amount')"
                  :disabled="!!claimType.requires_receipt && (!receiptDate || (!claimMonth && !!claimType.allow_advance_purchase && claimType.claim_entitlement_settings && claimType.claim_entitlement_settings.credit_unit === 'month'))"
                  data-vv-as="amount"
                  data-vv-name="amount"
                  label="Amount per unit"
                  prefix="RM"
                  :readonly="!!detail || (claimType.claim_entitlement_settings && claimType.claim_entitlement_settings.credit_unit === 'day' && !!claimType.claim_entitlement_settings.is_fixed_amount)"
                  outlined
                  @keydown.native="['-'].includes($event.key) && $event.preventDefault()"
                />
              </v-col>

              <v-col
                cols="4"
                class="px-2"
              >
                <v-text-field
                  v-money="money"
                  :value="totalAmount"
                  :disabled="true"
                  label="Total Amount"
                  prefix="RM"
                  outlined
                />
                
                <v-col
                  cols="12"
                  class="mt-n10"
                >
                  <span
                    v-if="isExceedLimit && !loading"
                    class="red--text text--lighten-1 caption"
                  >
                    {{ limitCheckingMessage }}
                  </span>
                </v-col>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col class="px-2">
                <v-textarea
                  v-model="description"
                  v-validate="claimType.is_description_mandatory ? 'required' : ''"
                  :error-messages="errors.collect('description')"
                  data-vv-name="description"
                  data-vv-as="description"
                  label="Description"
                  outlined
                  rows="3"
                  no-resize
                />
              </v-col>
            </v-row>

            <v-row
              v-show="claimType.requires_document"
              no-gutters
            >
              <v-col cols="12">
                <v-btn
                  :disabled="progressing"
                  class="mx-2"
                  @click="$refs.fileInput.click()"
                >
                  <v-icon 
                    small
                    left
                  >
                    mdi-paperclip
                  </v-icon>
                  Add Files
                </v-btn>
                <input
                  ref="fileInput"
                  type="file"
                  multiple
                  class="d-none"
                  accept=".jpg,.jpeg,.png,.pdf"
                  @change="onFilesSelected"
                >
                <input
                  v-validate="claimType.requires_document ? 'attachmentRequired' : ''"
                  type="hidden"
                  :value="attachments.length"
                  data-vv-name="file"
                >
                <div
                  v-if="attachments.length"
                  class="px-4"
                >
                  <v-row
                    v-for="(file, index) in attachments"
                    :key="index"
                  >
                    <v-chip
                      class="mt-2"
                      small
                      close
                      @click:close="removeFile(index)"
                      @click="previewFile(file)"
                    >
                      {{ file.name }}
                    </v-chip>
                  </v-row>
                </div>
                <div
                  v-else
                  class="px-4"
                >
                  <span>{{ 'No file chosen (.pdf, .jpg, .jpeg, .png)' }}</span>
                </div>

                <div
                  v-if="uploadErrors.length"
                  class="px-2"
                >
                  <span
                    v-for="(err, i) in uploadErrors"
                    :key="i"
                    class="red--text text-caption d-block"
                  >
                    {{ err }}
                  </span>
                </div>
                <span class="red--text text--lighten-1 caption px-4">{{ errors.first('file') }}</span>
              </v-col>
            </v-row>

            <v-row
              v-show="claimType.requires_declaration"
              no-gutters
            >
              <div class="d-flex align-start">
                <v-checkbox
                  v-model="isDeclarationAccepted"
                  color="green"
                  hide-details
                  class="mt-0 pa-0 mr-1"
                />
                <span
                  v-html="claimType.declaration_text"
                />
              </div>
            </v-row>
          </div>
        </v-container>
      </form>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="closeDialog"
        >
          Cancel
        </v-btn>
        <v-btn
          :loading="progressing"
          :disabled="loading || !claimType || isExceedLimit || (claimType && !!claimType.requires_declaration && !isDeclarationAccepted) || (claimType && !!claimType.has_limit && claimType.claim_entitlement_settings && claimType.claim_entitlement_settings.credit_unit === 'day' && noDateSelected)"
          color="hrissecondary"
          class="px-10 white--text"        
          @click="submitForm"
        >
          Save
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import { VMoney } from 'v-money'
  import { mapState, mapGetters } from 'vuex'
  export default {
    directives: { money: VMoney },
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      claimEntitlements: {
        type: Array,
        required: true,
      },
      claimRequest: {
        type: Object,
        required: true,
      },
      transactionDateValidity: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        loading: false,
        progressing: false,
        claimType: null,
        receiptDateMenu: false,
        receiptDate: null,
        receiptNumber: null,
        quantity: 1,
        money: {
          decimal: '.',
          thousands: ',',
          precision: 2,
          masked: false,
        },
        amountKey: 0,
        amount: 0,
        description: null,
        customFieldValues: {},
        datePickers: {},
        detail: null,
        limit: 0,
        noDateSelected: false,
        dateMenu: false,
        dates: [],
        dateClaims: [],
        today: new Date().toISOString().slice(0, 10),
        attachments: [],
        maxFiles: 5,
        allowedTypes: [
          'image/jpeg',
          'image/png',
          'application/pdf',
        ],
        uploadErrors: [],
        claimMonthMenu: false,
        claimMonth: null,
        includesEndDate: true,
        departmentEmployees: [],
        combineEntitlementEmployees: [],
        isDeclarationAccepted: false,
      }
    },
    computed: {
      ...mapGetters(['errors']),
      ...mapState(['currentEmployee']),
      maxReceiptDate () {
        const cycleEndDate = this.$moment(this.claimRequest.claim_cycle.end_date).format('YYYY-MM-DD')
        
        return this.$moment().isAfter(cycleEndDate) ? cycleEndDate : this.$moment().format('YYYY-MM-DD')
      },
      minReceiptDate () {
        const lastEndDay = this.transactionDateValidity[this.transactionDateValidity.length - 1].end_day
        return this.$moment().subtract(lastEndDay, 'days').format('YYYY-MM-DD')
      },
      isExceedLimit () {
        if (
          this.claimType && 
          !!this.claimType.has_limit &&
          !this.claimType.allow_exceed_limit && 
          this.claimType.claim_entitlement_settings && 
          (
            this.claimType.claim_entitlement_settings.credit_unit === 'year' || 
            (this.claimType.claim_entitlement_settings.credit_unit === 'day' && this.claimType.claim_entitlement_settings.allow_combine_entitlement && this.dates.length > 1) ||
            (this.claimType.claim_entitlement_settings.credit_unit === 'month' && this.claimMonth)
          )
        ) {
          const parsedTotal = parseFloat(this.totalAmount.toString().replace(/,/g, ''))
          const roundedAmount = Math.round(parsedTotal * 100)
          return roundedAmount > this.limit
        }

        return false
      },
      totalAmount () {
        const cleanAmount = parseFloat(this.amount.toString().replace(/,/g, '')) || 0
        const cleanQuantity = parseFloat(this.quantity) || 0
        return parseFloat(cleanAmount * cleanQuantity).toFixed(2)
      },
      limitCheckingMessage () {
        if (this.limit <= 0) return 'Insufficient balance or no entitlement'
        return `Do not exceed RM ${this.thousandSeparator((this.limit / 100).toFixed(2))}`
      },
      claimMonthDisplay () {
        return this.claimMonth ? this.$moment(this.claimMonth + '-01', 'YYYY-MM-DD').format('MMM YYYY') : ''
      },
      dateRangeDisplay () {
        if (Array.isArray(this.dates) && this.dates.length === 2) {
          const [start, end] = this.dates
          return `${start} - ${end}`
        } else {
          return ''
        }
      },
    },
    watch: {
      claimType (newType) {
        this.resetForm()
        if (newType) {
          if (newType.custom_fields && newType.custom_fields.length > 0) {
            newType.custom_fields.forEach(field => {
              if (this.customFieldValues[field.id] === undefined) {
                this.$set(this.customFieldValues, field.id, '')
              }
              if (field.type === 'Date') {
                this.$set(this.datePickers, field.id, false)
              }
            })
          }

          if (newType.details && newType.details.length > 0) {
            this.detail = newType.details[0]
            this.claimDetailChanged(newType.details[0])
          }

          if (!!newType.has_limit && newType.claim_entitlement_settings && newType.claim_entitlement_settings.credit_unit === 'day') {
            if (newType.claim_entitlement_settings.includes_end_date) {
              this.includesEndDate = true
            } else {
              this.includesEndDate = false
            }

            if (newType.claim_entitlement_settings.allow_claim_half_entitlement) {
              this.quantity = 0
            }

            this.amount = newType.claim_entitlement_settings.claim_entitlement_setting_details.length > 0 ? newType.claim_entitlement_settings.claim_entitlement_setting_details[0].max_amount : 0
          }

          if (!newType.requires_receipt && !!newType.has_limit) {
            if (newType.claim_entitlement_settings && newType.claim_entitlement_settings.credit_unit !== 'day' && Array.isArray(newType.claim_entitlement_settings.claim_entitlements)) {
              const claimCycleStartDate = new Date(this.claimRequest.claim_cycle.start_date)
              const claimCycleEndDate = new Date(this.claimRequest.claim_cycle.end_date)

              const sortedEntitlements = this.claimType.claim_entitlement_settings.claim_entitlements
                .slice()
                .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))

              const matchingEntitlement = sortedEntitlements.find(entitlement => {
                const startDate = new Date(entitlement.start_date)
                const endDate = new Date(entitlement.end_date)
                return startDate <= claimCycleEndDate && claimCycleStartDate <= endDate
              })

              if (matchingEntitlement) {
                this.limit = matchingEntitlement.current_available - matchingEntitlement.pending_amt - matchingEntitlement.claimed_amount + matchingEntitlement.adjusted_amount
              }
            }
          }
          
          if (newType.claim_entitlement_settings && newType.claim_entitlement_settings.credit_unit === 'day' && !!newType.claim_entitlement_settings.allow_combine_entitlement && this.departmentEmployees.length <= 0) {
            this.loadEmployees()
          }
        }
      },
      dates (dates) {
        if (this.claimType && !!this.claimType.has_limit && this.claimType.claim_entitlement_settings && this.claimType.claim_entitlement_settings.credit_unit === 'day') {
          this.limit = this.claimType.claim_entitlement_settings.claim_entitlements
            .filter(e => dates.includes(e.start_date))
            .reduce((total, e) => total + e.balance, 0)

          if (dates.length <= 0) {
            this.noDateSelected = true
            this.dateClaims = []
            if (this.claimType.claim_entitlement_settings.allow_claim_half_entitlement) {
              this.quantity = dates.length
            }
          } else {
            this.noDateSelected = false
            
            if (this.claimType.claim_entitlement_settings.allow_claim_half_entitlement) {
              const datesInRange = this.getAllDatesInRange()
              const entitlement = this.claimType.claim_entitlement_settings.claim_entitlement_setting_details[0]
              const maxAmount = entitlement ? entitlement.max_amount : 0
              this.amount = maxAmount / 100

              this.dateClaims = datesInRange.map(date => {
                return {
                  date: date,
                  base_amount: maxAmount / 100,
                  amount_to_deduct: maxAmount / 100,
                  is_half_entitlement: false,
                }
              })

              this.recalculateTotals()
            }

            if (this.claimType.claim_entitlement_settings.allow_combine_entitlement) {
              this.getEntitlementBalance(this.combineEntitlementEmployees)
            }
          }
        }
      },
      receiptDate (receipt) {
        if (
          this.claimType && this.claimType.claim_entitlement_settings &&
          this.claimType.claim_entitlement_settings.credit_unit !== 'day' &&
          Array.isArray(this.claimType.claim_entitlement_settings.claim_entitlements) && 
          !(this.claimType.claim_entitlement_settings.credit_unit === 'month' && this.claimType.allow_advance_purchase)
        ) {
          this.limit = 0
          
          const matchingEntitlement = this.claimType.claim_entitlement_settings.claim_entitlements.find(entitlement => {
            const startDate = new Date(entitlement.start_date)
            const endDate = new Date(entitlement.end_date)
            return startDate <= new Date(receipt) && new Date(receipt) <= endDate
          })

          if (matchingEntitlement) {
            this.limit = matchingEntitlement.current_available - matchingEntitlement.pending_amt - matchingEntitlement.claimed_amount + matchingEntitlement.adjusted_amount
          }
        }
      },
      claimMonth (month) {
        this.limit = 0
        const monthSelected = month + '-01'

        if (
          this.claimType && this.claimType.claim_entitlement_settings &&
          this.claimType.claim_entitlement_settings.credit_unit === 'month' &&
          Array.isArray(this.claimType.claim_entitlement_settings.claim_entitlements)
        ) {
          const matchingEntitlement = this.claimType.claim_entitlement_settings.claim_entitlements.find(entitlement => {
            return entitlement.start_date === monthSelected
          })

          if (matchingEntitlement) {
            this.limit = matchingEntitlement.current_available - matchingEntitlement.pending_amt - matchingEntitlement.claimed_amount + matchingEntitlement.adjusted_amount
          }
        }
      },
      combineEntitlementEmployees (employees) {
        if (!this.dates || this.dates.length === 0) return

        // Clear previous timeout if still waiting
        clearTimeout(this._combineEntitlementTimeout)

        this._combineEntitlementTimeout = setTimeout(() => {
          if (
            this.claimType && this.claimType.has_limit && !this.claimType.allow_exceed_limit &&
            this.claimType.claim_entitlement_settings.credit_unit === 'day' &&
            this.claimType.claim_entitlement_settings.allow_combine_entitlement
          ) {
            this.getEntitlementBalance(employees)
          }
        }, 1000) // 1 second
      },
    },
    created () {
      this.$validator.extend('validAmount', {
        getMessage (field, args) {
          return 'Should more than 0'     
        },
        validate (value, args) {
          return value.toString().replaceAll(',', '') > 0
        },
      })
      this.$validator.extend('checkDecimalPlace', {
        getMessage (field) {
          return 'Only allow up to 3 decimal places'
        },
        validate (value) {
          const cleanValue = value.toString().replace(/,/g, '')
          const regex = /^\d+(\.\d{1,3})?$/

          return regex.test(cleanValue)
        },
      })
      this.$validator.extend('attachmentRequired', {
        getMessage (field) {
          return 'Please provide at least one supporting document'
        },
        validate (value) {
          return Number(value) > 0
        },
      })
    },
    methods: {
      getEntitlementBalance (employees) {
        if (this.dates.length > 1) {
          this.loading = true
          this.$api.claimEntitlements().getTotalEntitlements(this.prepareGetTotalEntitlementFormData(employees))
            .then(res => {
              this.limit = res.data
              this.loading = false
            })
        }
      },
      loadEmployees () {
        this.loading = true
        this.$api.hrisEmployees().list('?claimRequestId=' + this.claimRequest.id)
          .then((res) => {
            this.departmentEmployees = _.orderBy(res.data, ['person.name'], ['asc'])

            this.loading = false
          })
      },
      prepareGetTotalEntitlementFormData (employees) {
        let allDates = this.getAllDatesInRange()

        if (
          this.claimType && this.claimType.claim_entitlement_settings && 
          this.claimType.claim_entitlement_settings.credit_unit === 'day' &&
          !this.claimType.claim_entitlement_settings.includes_end_date &&
          allDates.length > 1
        ) {
          allDates = allDates.slice(0, -1)
        }

        return {
          employees: employees,
          claim_type_id: this.claimType.id,
          dates: allDates,
        }
      },
      formatMoney (value) {
        if (!value) return 'RM 0.00'
        // v-money-like formatting
        return Number(value).toLocaleString('en-MY', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      },
      recalculateTotals () {
        // adjust amount_to_deduct based on half/full
        this.dateClaims.forEach(c => {
          c.amount_to_deduct = c.is_half_entitlement
            ? c.base_amount / 2
            : c.base_amount
        })
        
        // quantity = sum of full (1) + half (0.5)
        this.quantity = this.dateClaims.reduce(
          (sum, c) => sum + (c.is_half_entitlement ? 0.5 : 1),
          0
        )
      },
      getAllDatesInRange () {
        const [start, end] = this.dates
        if (!start || !end) return []

        const startDate = this.$moment(start)
        const endDate = this.$moment(end)
        const dateList = []

        while (startDate.isSameOrBefore(endDate)) {
          dateList.push(startDate.format('YYYY-MM-DD'))
          startDate.add(1, 'day')
        }

        return dateList
      },
      allowedDates (date) {
        // if only the first date (start) is selected
        if (this.dates.length === 1) {
          const start = this.dates[0]
          // disable any date *before* the start
          return date >= start
        }
        // otherwise allow all dates (default)
        return true
      },
      closeDialog () {
        this.resetForm()
        this.description = null
        this.attachments = []
        this.uploadErrors = []
        this.claimType = null
        this.dialog = false
      },
      resetForm () {
        this.$validator.reset()
        this.limit = 0
        this.quantity = 1
        this.amount = 0
        this.amountKey++
        this.receiptNumber = null
        this.receiptDateMenu = false
        this.customFieldValues = {}
        this.datePickers = {}
        this.detail = null
        this.receiptDate = null
        this.claimMonth = null
        this.dates = []
        this.dateClaims = []
        this.combineEntitlementEmployees = []
        this.includesEndDate = true
        this.isDeclarationAccepted = false
      }, 
      claimDetailChanged (obj) {
        if (obj && obj.amount) {
          const rawAmount = obj.amount / 100

          this.amount = rawAmount.toFixed(2)
          this.amountKey++
        }
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.claimRequestEntries().create(this.prepareFormData())
              .then((res) => {
                this.$emit('created')
                this.$snack({ message: res.data.status })

                this.closeDialog()
              })
              .catch(error => {
                if (error.response.status === 500) {
                  this.$snack({
                    color: 'error',
                    message: 'A server error occurred. Please try again later', 
                  })
                } else {
                  this.$snack({
                    color: 'error',
                    message: 'Error! Please contact IT support', 
                  })
                }
              })
              .finally(() => {
                this.progressing = false
              })
          })
      },
      prepareFormData () {
        let formData = new FormData()
        const datesInRange = this.getAllDatesInRange()

        formData.append('claim_request_id', this.claimRequest.id)
        formData.append('claim_type_id', this.claimType.id)
        formData.append('includes_end_date', this.includesEndDate)
        formData.append('quantity', this.quantity)
        formData.append('amount', (this.amount.toString().replace(/,/g, '') * 100))
        formData.append('claim_entitlement_dates', JSON.stringify(datesInRange))
        formData.append('claim_entitlement_dates_with_amount', JSON.stringify(this.dateClaims))
        formData.append('combine_with', JSON.stringify(this.combineEntitlementEmployees))

        if (this.claimType) {
          if (this.claimType.requires_receipt) {
            formData.append('receipt_date', this.receiptDate)
            formData.append('receipt_number', this.receiptNumber)
          }

          if (this.claimType.claim_entitlement_settings && this.claimType.claim_entitlement_settings.credit_unit === 'month') {
            formData.append('entitlement_date_to_deduct', this.claimMonth + '-01')
          }

          if (this.claimType.requires_declaration) {
            formData.append('declaration_accepted', this.isDeclarationAccepted ? 1 : 0)
          }
        }

        if (this.detail) {
          formData.append('claim_type_detail_id', this.detail.id)
        }

        if (this.description) {
          if (this.detail) {
            formData.append('description', 'Claim for ' + this.detail.name + '.</br>' + this.description)
          } else {
            formData.append('description', this.description)
          }
        }
        
        if (this.customFieldValues) {
          formData.append(
            'custom_fields', 
            JSON.stringify(
              Object.entries(this.customFieldValues).map(([id, value]) => ({
                id: Number(id),
                value,
              }))
            )
          )
        }

        if (this.attachments && this.attachments.length > 0) {
          this.attachments.forEach((file) => {
            formData.append('files[]', file)
          })
        }

        return formData
      },
      onDateSelected (field) {
        this.datePickers[field.id] = false
        this.$nextTick(() => {
          this.$validator.validate(`custom_field_${field.id}`)
        })
      },
      thousandSeparator (amount) {
        if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
          return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        } else {
          return amount
        }
      },
      onFilesSelected (e) {
        const files = Array.from(e.target.files)
        let newFiles = []
        this.uploadErrors = []

        for (const file of files) {
          // check invalid file type
          if (!this.allowedTypes.includes(file.type)) {
            this.uploadErrors.push(`Invalid file type: ${file.name}`)
            continue
          }

          // check file limit
          if (this.attachments.length + newFiles.length >= this.maxFiles) {
            this.uploadErrors.push(`You can only upload up to ${this.maxFiles} files.`)
            break
          }

          file.preview = URL.createObjectURL(file)
          newFiles.push(file)
        }

        if (newFiles.length) {
          this.attachments.push(...newFiles)
        }

        // reset native file input so user can re-select same file(s) later after being removed
        e.target.value = ''

        this.validateFile()
      },
      removeFile (index) {
        const file = this.attachments[index]
        if (file.preview) {
          URL.revokeObjectURL(file.preview) // cleanup
        }
        this.attachments.splice(index, 1)
        this.uploadErrors = []

        this.validateFile()
      },
      validateFile () {
        if (this.claimType && this.claimType.requires_document) {
          if (this.attachments.length > 0) {
            this.errors.remove('file')
          } else {
            this.$validator.validate('file')
          }
        }
      },
      previewFile (file) {
        if (file.preview) {
          window.open(file.preview, '_blank')
        }
      },
    },
  }
</script>

<style lang="css" scoped>
.card-overflow {
  height: 40vh;
  overflow-y: scroll;
}
::-webkit-scrollbar {
  width: 0px;  /* Remove scrollbar space */
  background: transparent;  /* Optional: just make scrollbar invisible */
}
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}
.text-editor-toolbar-height>>> .v-toolbar__content{
  height: unset !important;
  min-height: 42px;
}
.text-editor-toolbar-height>>>.v-sheet.v-sheet--tile.theme--light.v-toolbar.v-toolbar--dense.v-toolbar--flat.grey.lighten-4{
  min-height:unset !important;
}
</style>
